// src/pages/PrivacyPolicy.tsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // npm i react-helmet-async
import Title from "../components/typography/Title";

export const LAST_UPDATED_ISO = "2025-12-03"; // YYYY-MM-DD
export const LAST_UPDATED_READABLE = "03 Dec 2025";

export default function PrivacyPolicy() {
  const canonical = "https://babblebots.ai/privacy-policy";

  // JSON-LD for rich results / LLMs
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "PrivacyPolicy",
      name: "Babblebots Privacy Policy",
      url: canonical,
      dateModified: LAST_UPDATED_ISO,
      inLanguage: "en",
      publisher: {
        "@type": "Organization",
        name: "Mystina AI Labs Private Limited",
        url: "https://babblebots.ai",
        logo: {
          "@type": "ImageObject",
          url: "https://babblebots.ai/logo.png",
        },
      },
    }),
    [canonical]
  );

  return (
    <div className="bb2-page">
      <Helmet>
        <title>Privacy Policy | Babblebots.ai</title>
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index,follow" />
        <meta
          name="description"
          content="Learn how Babblebots.ai collects, uses, and protects your information when you use our website and services."
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | Babblebots.ai" />
        <meta
          property="og:description"
          content="Our commitment to privacy, transparency, and your data rights."
        />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:image"
          content="https://babblebots.ai/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Babblebots.ai" />
        <meta
          name="twitter:description"
          content="Our commitment to privacy, transparency, and your data rights."
        />
        <meta
          name="twitter:image"
          content="https://babblebots.ai/og-image.jpg"
        />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bb2-container">
        <div className="bb2-glass">
          <Title size="lg" as="h1">
            Privacy Policy
          </Title>
          <div className="bb2-meta">
            <span>Last updated:</span>
            <time dateTime={LAST_UPDATED_ISO}>{LAST_UPDATED_READABLE}</time>
          </div>

          <hr className="bb2-divider" />

          {/* Table of contents (anchors) */}
          <nav
            className="bb2-prose"
            aria-label="Table of contents"
            style={{ marginBottom: 8 }}
          >
            <ul>
              <li>
                <a href="#scope">1. Scope of this Privacy Policy</a>
              </li>
              <li>
                <a href="#collect">2. How We Collect Your Data</a>
              </li>
              <li>
                <a href="#data">3. Data We Collect and Process</a>
              </li>
              <li>
                <a href="#use">4. How We Use Your Data</a>
              </li>
              <li>
                <a href="#disclose">5. How We Disclose Your Data</a>
              </li>
              <li>
                <a href="#automated">6. How We May Use Automated Processing</a>
              </li>
              <li>
                <a href="#cookies">7. Cookies and Similar Technology</a>
              </li>
              <li>
                <a href="#children">8. Children</a>
              </li>
              <li>
                <a href="#changes">9. Changes to Privacy Policy</a>
              </li>
              <li>
                <a href="#contact">10. Contact Us</a>
              </li>
            </ul>
          </nav>

          <article className="bb2-prose">
            <p>
              Mystina AI Labs Private Limited (“Babblebots”) is committed to protecting your
              privacy and to transparency regarding the data about you that we
              collect and how we use it. This Privacy Policy is designed to let
              you know the personal data and/or personal information about you
              that Mystina AI Labs Private Limited and its subsidiaries and affiliates, doing
              business as Babblebots.ai (“Babblebots,” “we,” “us,” “our”),
              collect and process when you visit our website (“Website”) or use
              the services and applications we offer (“Services”). We refer to
              our Website and Services together in this Privacy Policy as our
              “Offerings.”
            </p>
            <p>
              This Privacy Policy applies to our collection and use of your
              personal data or personal information (or equivalent terms). When
              we refer to your “data” or your “information” in this Privacy
              Policy, we are referring to personal data or personal information
              as defined by applicable law. We use those terms interchangeably
              to refer to data or information that relates to you as an
              identifiable person, that identifies, relates to, or describes
              you, or that is reasonably capable of being associated with or
              linked to you. Please read this Privacy Policy carefully to
              understand our practices and your rights in your data. If you do
              not agree with this Privacy Policy in general or any part of it,
              you should not access or use our Offerings.
            </p>

            <h2 id="scope">1. SCOPE OF THIS PRIVACY POLICY</h2>
            <p>
              There are several ways to use our Offerings or we may collect and
              process your data. For example, you might visit our Website, or
              you might use our Services because you have been invited or
              granted access to our Services by a Babblebots business customer
              who uses our Services (an “Employer”). The data we collect and
              process and how we use that data depends on how you use or the
              Employer uses our Offerings. Some ways we might collect and
              process your data are when you are a:
            </p>
            <ul>
              <li>Visitor to our Website;</li>
              <li>
                User using our Services through an Employer that authorized your
                access to our Services under the Employer’s account;
              </li>
              <li>
                Candidate who has been sent to use our Services by an Employer
                using our Services for the Employer’s employment-related and/or
                engagement purposes.
              </li>
            </ul>

            <h2 id="collect">2. HOW WE COLLECT YOUR DATA</h2>
            <p>We collect and process your data in the following ways:</p>
            <ul>
              <li>
                <strong>Automatically.</strong> We collect and process certain
                data automatically when you use our Offerings. This may include
                technical information about your use of the Offerings and the
                pages you visit, actions you take, and features you use when
                interacting with our Offerings.
              </li>
              <li>
                <strong>Provided By You.</strong> We collect and process the
                data you provide to us when you sign up for, access, use, or
                request support for our Offerings (e.g., account creation,
                forms, support requests, Candidate Features).
              </li>
              <li>
                <strong>Provided By Employers.</strong> If you are a User or
                Candidate, we collect and process any data that the Employer
                inviting or authorizing you to use our Services provides to us.
              </li>
            </ul>

            <h2 id="data">3. DATA WE COLLECT AND PROCESS</h2>
            <p>
              The types of data we collect and process depend on which of our
              Offerings you use and how you use those Offerings.{" "}
              <em>Data Processing Agreement</em>
            </p>

            <h3>3.1. Data We Automatically Collect</h3>
            <p>
              IP address and Internet/electronic activity (connection info,
              browser type/version, features accessed, pages visited,
              preferences, interactions). We may use log files, cookies, and
              similar tech. See “Cookies and Similar Technology” below.
            </p>

            <h3>3.2. Data You Provide to Us</h3>
            <ul>
              <li>
                <strong>Identifiers</strong> — name, alias, username, email,
                non-precise location, social profiles, phone, account name.
              </li>
              <li>
                <strong>Professional or Employment Information</strong> — job
                title/role, experience, employer.
              </li>
              <li>
                <strong>Characteristics</strong> — gender, racial/ethnic origin
                (if voluntarily provided at the Employer’s request as part of
                Candidate Features).
              </li>
              <li>
                <strong>Education Information</strong> — institutions,
                certifications, degrees.
              </li>
              <li>
                <strong>Interactions and Results</strong> — inputs,
                interactions, audio/visual and written content generated by your
                use of Candidate Features, and results (e.g., scores/rankings).
              </li>
              <li>
                <strong>Other Provided Data</strong> — any other data you share
                with us.
              </li>
              <li>
                <strong>Voice (Candidates only)</strong> — collected/processed
                for authentication and assessment if you consent.
              </li>
              <li>
                <strong>Facial Image (Candidates only)</strong> —
                collected/processed for facial matching/detection only if you
                consent and the Employer enables the feature.
              </li>
            </ul>

            <h3>3.3. Data Received From Third Parties</h3>
            <p>
              We may process data received from third parties on behalf of
              Employers (e.g., internal data-sharing networks, public records,
              open-source datasets). This may include identifiers, employment,
              characteristics, and education info (e.g., name, qualifications,
              work history, contact info).
            </p>

            <h2 id="use">4. HOW WE USE YOUR DATA</h2>
            <h3>4.1. Purpose of Processing</h3>
            <ul>
              <li>
                Provide, develop, maintain, enhance, and support our Offerings
                or to perform a contract with an Employer;
              </li>
              <li>Provide our Services to the applicable Employer;</li>
              <li>
                Detect, prevent, mitigate, or repair errors/technical issues or
                fraud/illegal activity;
              </li>
              <li>
                Ensure the Offerings are used in compliance with applicable
                terms and agreements;
              </li>
              <li>
                Communicate with you (inquiries, transactional notices, service
                info);
              </li>
              <li>
                Understand usage to enhance, optimize, or personalize the
                Offerings;
              </li>
              <li>
                Use anonymized/de-identified gender or racial/ethnic origin
                information to ensure Services are unbiased and compliant;
              </li>
              <li>
                Comply with legal obligations and law-enforcement requests;
              </li>
              <li>
                Act in our legitimate interests or other business purposes
                permitted by law;
              </li>
              <li>Other specific purposes to which you have consented.</li>
            </ul>

            <h3>4.2. Processing Duration</h3>
            <p>
              For Visitors, we process only as long as permitted by law. For
              Users/Candidates, we process as permitted by our agreement with
              the Employer or as required by law.
            </p>

            <h3>4.3. Data Subject Rights and Request</h3>
            <p>
              Subject to our agreement with the Employer and applicable law,
              Users/Candidates may have the:
            </p>
            <ul>
              <li>Right of Access</li>
              <li>Right to Rectification</li>
              <li>Right to Erasure</li>
              <li>Right to Restrict Processing</li>
              <li>Right to Data Portability</li>
              <li>Right to Object to Processing</li>
            </ul>
            <p>
              Users/Candidates may request deletion/update as permitted by our
              agreement with the Employer or law.
              {/* If you host a form, link it here: */}
              <a href="#" rel="nofollow">
                {" "}
                Data subject request form
              </a>
            </p>

            <h2 id="disclose">5. HOW WE DISCLOSE YOUR DATA</h2>
            <ul>
              <li>
                <strong>Employers</strong> — if you are a Candidate or User, we
                disclose your data (including Results) to the applicable
                Employer.
              </li>
              <li>
                <strong>Platform Service Providers</strong> — cloud, LLM,
                speech/image processing providers, etc.
              </li>
              <li>
                <strong>Other Third-Party Providers</strong> — analytics,
                authentication, technical/security, fraud prevention, product
                development.
              </li>
              <li>
                <strong>Integrations</strong> — as determined by the Employer
                when it connects third-party systems.
              </li>
              <li>
                <strong>Legal/Compliance</strong> — to comply with laws, enforce
                agreements, or protect rights/safety.
              </li>
              <li>
                <strong>Our Affiliates</strong> — within our corporate group per
                this Policy.
              </li>
              <li>
                <strong>Business Transfers</strong> — merger/asset sale
                situations with safeguards.
              </li>
              <li>
                <strong>With Your Consent</strong> — for specific purposes you
                approve.
              </li>
            </ul>

            <h2 id="automated">6. HOW WE MAY USE AUTOMATED PROCESSING</h2>
            <h3>6.1. AI Features Generally</h3>
            <ul>
              <li>
                Employer decides how to interpret/use any results. Babblebots
                does not reject candidates based on AI features.
              </li>
              <li>We do not use AI features to uniquely identify you.</li>
              <li>
                We do not train our models on your personal data, nor authorize
                providers to train on your data.
              </li>
            </ul>

            <h3>6.2. Candidate Detection AI Features</h3>
            <p>
              Our AI features may analyze voice/video, facial images, and
              interaction patterns to detect potential copying, unauthorized
              assistance, or behavior anomalies during Candidate Features.
              Participation is opt-in; if you do not wish to use the Services,
              contact the Employer for alternatives.
            </p>

            <h3>6.3. Other Automated Processing</h3>
            <p>
              We and service providers may use automated
              processing/decision-making where necessary to perform a contract
              with you or to detect/prevent fraud, abuse, or violations. By
              using our Services, you consent to such processing.
            </p>

            <h2 id="cookies">7. COOKIES AND SIMILAR TECHNOLOGY</h2>
            <p>
              Babblebots uses cookies (and similar tech) to identify areas of
              the Website visited, enhance performance, and support
              functionality. Most browsers allow you to disable cookies, but
              doing so may break parts of the site. We do not place PII in
              cookies.
            </p>

            <h3>7. Cookie Policy</h3>
            <h4>7.1. Introduction</h4>
            <p>
              This Cookie Policy explains what cookies are, how we use them, and
              your choices.
            </p>

            <h4>7.2. What are Cookies?</h4>
            <p>
              Small text files stored on your device that help sites function
              and provide analytics.
            </p>

            <h4>7.3. Our Use of Cookies</h4>
            <p>
              We use third-party services that may place cookies: Google Tag
              Manager (tag management, analytics), Vimeo (embedded video),
              Calendly (scheduling), and WordPress (if used for
              auth/sessions/preferences). See each provider’s privacy policy for
              details.
            </p>

            <h4>7.4. Your Choices Regarding Cookies</h4>
            <p>
              By using our website, you consent to cookies by us and our service
              providers. If you do not accept cookies, you will not be able to
              access the website.
            </p>

            <h2 id="children">8. CHILDREN</h2>
            <p>
              We do not knowingly collect personal data from children under 16.
              If we learn we have done so, we will delete it as soon as possible
              and take required measures.
            </p>

            <h2 id="changes">9. CHANGES TO PRIVACY POLICY</h2>
            <p>
              We may update this Policy from time to time. The “Last updated”
              date above reflects the latest revision. Updates are effective
              upon posting.
            </p>

            <h2 id="contact">10. CONTACT US</h2>
            <p>
              Don’t hesitate to contact us if you have any questions.
              <br />
              Email:{" "}
              <a href="mailto:helpme@babblebots.ai">helpme@babblebots.ai</a>
              <br />
              Data Protection Officer (DPO): Akriti Ghosh
              {/* <a href="mailto:helpme@babblebots.ai">helpme@babblebots.ai</a> */}
            </p>
          </article>
        </div>
      </div>
    </div>
  );
}
